import { useEffect, useState } from "react";
import { Plus, Trash2, Edit2, Database, ChevronRight, ArrowLeft, Save, X } from "lucide-react";

const API = `${import.meta.env.BASE_URL}api`;

type Field = { key: string; label: string; type: "text" | "longtext" | "number" | "url" | "image" | "richtext" | "boolean"; required?: boolean };
type Collection = { id: number; name: string; slug: string; description: string | null; fields: Field[] };
type Entry = { id: number; collectionId: number; slug: string; title: string; data: Record<string, any>; published: boolean };

const FIELD_TYPES: Field["type"][] = ["text", "longtext", "number", "url", "image", "richtext", "boolean"];

export default function AdminCollections() {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  const load = () => fetch(`${API}/admin/collections`, { credentials: "include" }).then(r => r.json()).then(setCollections);
  useEffect(() => { load(); }, []);

  if (openId != null) {
    return <CollectionDetail id={openId} onBack={() => { setOpenId(null); load(); }} />;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Database className="w-6 h-6 text-primary" /> Collections</h1>
          <p className="text-sm text-slate-500 mt-1">Define content types like Cities or Services, then use their entries in programmatic SEO templates.</p>
        </div>
        <button onClick={() => setCreating(true)} className="px-4 py-2 bg-primary text-white rounded-lg flex items-center gap-2 font-medium hover:opacity-90"><Plus className="w-4 h-4" /> New Collection</button>
      </div>

      {creating && <CreateCollectionModal onClose={() => setCreating(false)} onCreated={(c) => { setCreating(false); load(); setOpenId(c.id); }} />}

      {collections.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-dashed border-slate-300">
          <Database className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No collections yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {collections.map(c => (
            <button key={c.id} onClick={() => setOpenId(c.id)} className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-primary hover:shadow-sm transition">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">{c.name}</h3>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
              <p className="text-xs text-slate-500 mt-1">slug: <code className="text-primary">{c.slug}</code></p>
              <p className="text-xs text-slate-400 mt-2">{c.fields.length} field{c.fields.length === 1 ? "" : "s"}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateCollectionModal({ onClose, onCreated }: { onClose: () => void; onCreated: (c: Collection) => void }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!name) return;
    setSaving(true);
    const r = await fetch(`${API}/admin/collections`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name, slug, fields: [{ key: "name", label: "Name", type: "text", required: true }] }) });
    if (r.ok) onCreated(await r.json()); else setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-4">New Collection</h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500">Name</label>
            <input value={name} onChange={e => { setName(e.target.value); if (!slug) setSlug(e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")); }} placeholder="Cities" className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Slug (used in templates)</label>
            <input value={slug} onChange={e => setSlug(e.target.value)} placeholder="cities" className="w-full px-3 py-2 border border-slate-200 rounded-lg font-mono text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-slate-600">Cancel</button>
          <button onClick={submit} disabled={!name || saving} className="px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50">{saving ? "Creating..." : "Create"}</button>
        </div>
      </div>
    </div>
  );
}

function CollectionDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const [coll, setColl] = useState<Collection | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [tab, setTab] = useState<"entries" | "schema">("entries");
  const [editingEntry, setEditingEntry] = useState<Entry | "new" | null>(null);

  const load = async () => {
    const [c, e] = await Promise.all([
      fetch(`${API}/admin/collections/${id}`, { credentials: "include" }).then(r => r.json()),
      fetch(`${API}/admin/collections/${id}/entries`, { credentials: "include" }).then(r => r.json()),
    ]);
    setColl(c); setEntries(e);
  };
  useEffect(() => { load(); }, [id]);

  if (!coll) return <div className="p-10 text-center text-slate-400">Loading...</div>;

  const saveCollection = async (patch: Partial<Collection>) => {
    const r = await fetch(`${API}/admin/collections/${id}`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(patch) });
    if (r.ok) setColl(await r.json());
  };

  const deleteCollection = async () => {
    if (!confirm(`Delete "${coll.name}" and ALL its entries? This cannot be undone.`)) return;
    await fetch(`${API}/admin/collections/${id}`, { method: "DELETE", credentials: "include" });
    onBack();
  };

  const deleteEntry = async (eid: number) => {
    if (!confirm("Delete this entry?")) return;
    await fetch(`${API}/admin/entries/${eid}`, { method: "DELETE", credentials: "include" });
    load();
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-primary flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4" /> Back to collections</button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{coll.name}</h1>
          <p className="text-sm text-slate-500">slug: <code className="text-primary">{coll.slug}</code> · {entries.length} entries</p>
        </div>
        <button onClick={deleteCollection} className="px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 rounded-lg flex items-center gap-1"><Trash2 className="w-4 h-4" /> Delete</button>
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-4">
        {(["entries", "schema"] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-medium border-b-2 capitalize ${tab === t ? "border-primary text-primary" : "border-transparent text-slate-500"}`}>{t}</button>
        ))}
      </div>

      {tab === "schema" && (
        <SchemaEditor fields={coll.fields} onSave={fields => saveCollection({ fields })} />
      )}

      {tab === "entries" && (
        <>
          <div className="flex justify-end mb-3"><button onClick={() => setEditingEntry("new")} className="px-3 py-1.5 bg-primary text-white rounded-lg flex items-center gap-1 text-sm font-medium"><Plus className="w-4 h-4" /> Add Entry</button></div>
          {entries.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-dashed border-slate-300 text-slate-400">No entries yet.</div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {entries.map(e => (
                <div key={e.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2"><span className="font-medium text-slate-900 truncate">{e.title}</span>{!e.published && <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">draft</span>}</div>
                    <div className="text-xs text-slate-500 font-mono">{e.slug}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditingEntry(e)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => deleteEntry(e.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {editingEntry && (
        <EntryEditor collection={coll} entry={editingEntry === "new" ? null : editingEntry} onClose={() => { setEditingEntry(null); load(); }} />
      )}
    </div>
  );
}

function SchemaEditor({ fields, onSave }: { fields: Field[]; onSave: (f: Field[]) => void }) {
  const [local, setLocal] = useState<Field[]>(fields);
  useEffect(() => setLocal(fields), [fields]);
  const upd = (i: number, p: Partial<Field>) => setLocal(local.map((f, j) => j === i ? { ...f, ...p } : f));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2">
      <div className="grid grid-cols-[1fr_1fr_140px_80px_40px] gap-2 text-xs font-semibold uppercase text-slate-500 px-2">
        <span>Key</span><span>Label</span><span>Type</span><span>Required</span><span></span>
      </div>
      {local.map((f, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_140px_80px_40px] gap-2 items-center">
          <input value={f.key} onChange={e => upd(i, { key: e.target.value.replace(/[^a-zA-Z0-9_]/g, "") })} className="px-2 py-1.5 border border-slate-200 rounded text-sm font-mono" placeholder="population" />
          <input value={f.label} onChange={e => upd(i, { label: e.target.value })} className="px-2 py-1.5 border border-slate-200 rounded text-sm" placeholder="Population" />
          <select value={f.type} onChange={e => upd(i, { type: e.target.value as any })} className="px-2 py-1.5 border border-slate-200 rounded text-sm">
            {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <input type="checkbox" checked={!!f.required} onChange={e => upd(i, { required: e.target.checked })} className="mx-auto" />
          <button onClick={() => setLocal(local.filter((_, j) => j !== i))} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="flex justify-between pt-2">
        <button onClick={() => setLocal([...local, { key: "", label: "", type: "text" }])} className="text-sm text-primary font-medium">+ Add field</button>
        <button onClick={() => onSave(local.filter(f => f.key))} className="px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1"><Save className="w-4 h-4" /> Save schema</button>
      </div>
    </div>
  );
}

function EntryEditor({ collection, entry, onClose }: { collection: Collection; entry: Entry | null; onClose: () => void }) {
  const [title, setTitle] = useState(entry?.title || "");
  const [slug, setSlug] = useState(entry?.slug || "");
  const [data, setData] = useState<Record<string, any>>(entry?.data || {});
  const [published, setPublished] = useState(entry?.published ?? true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title) return;
    setSaving(true);
    const body = { title, slug, data, published };
    const url = entry ? `${API}/admin/entries/${entry.id}` : `${API}/admin/collections/${collection.id}/entries`;
    const r = await fetch(url, { method: entry ? "PATCH" : "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    setSaving(false);
    if (r.ok) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl mx-auto p-6 my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">{entry ? "Edit" : "New"} {collection.name} Entry</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500">Title</label>
              <input value={title} onChange={e => { setTitle(e.target.value); if (!entry && !slug) setSlug(e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Slug</label>
              <input value={slug} onChange={e => setSlug(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg font-mono text-sm" />
            </div>
          </div>
          {collection.fields.map(f => (
            <div key={f.key}>
              <label className="text-xs text-slate-500">{f.label}{f.required && <span className="text-rose-500"> *</span>}</label>
              {f.type === "longtext" || f.type === "richtext" ? (
                <textarea value={data[f.key] || ""} onChange={e => setData({ ...data, [f.key]: e.target.value })} rows={4} className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
              ) : f.type === "boolean" ? (
                <input type="checkbox" checked={!!data[f.key]} onChange={e => setData({ ...data, [f.key]: e.target.checked })} className="mt-2 block" />
              ) : f.type === "number" ? (
                <input type="number" value={data[f.key] ?? ""} onChange={e => setData({ ...data, [f.key]: e.target.value === "" ? null : Number(e.target.value) })} className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
              ) : (
                <input value={data[f.key] || ""} onChange={e => setData({ ...data, [f.key]: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
              )}
            </div>
          ))}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} /> Published</label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-slate-600">Cancel</button>
          <button onClick={save} disabled={!title || saving} className="px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50 flex items-center gap-1"><Save className="w-4 h-4" /> {saving ? "Saving..." : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
